import { ClipboardCopyButton } from '@neo/blocks/clipboard-copy-button'
import type { CustomCompType, GenericFieldConfig, GenericFormType } from '@neo/blocks/forms/types'
import { DraggableList, DragListIdKeys } from '@neo/blocks/sortable-list'
import { Button } from '@neo/index'
import { Input, PasswordInput } from '@neo/primitives/input'
import { Select } from '@neo/primitives/select'
import { Tooltip } from '@neo/primitives/tooltip'
import { ExternalLink, Info, XIcon } from 'lucide-react'
import { useParams } from 'router-v6'
import { v4 as uuidv4 } from 'uuid'
import { queryClient } from 'v2/configs/rq-setup'
import { useGetSlackChannelsQuery, useGetSlackCreateAppLinkQuery } from 'v2/pages/flow/queries'
import { ActiveFlowSelect } from 'v2/shared/components/flow-select'
import { RestrictedTokenField } from 'v2/shared/components/restricted-token-notice'
import { flowQueryKeys } from 'v2/shared/utils/query-key-management'
import { FlowTypes } from 'v2/types'
import { z } from 'zod'
import { ApiRequestVarsWrapper } from '../components/api-request-vars-wrapper'
import { type EventCallback, EventCallbacks } from '../components/event-callbacks'
import { SlackChannelTypes } from '../config/trigger-node.config'
import { v2validations } from '../config/validation.config'
import { useStartNodeAuthData } from '../hooks/use-start-node-auth-data'
import { useStartNodeType } from '../hooks/use-start-node-type'
import type { FlowData, SidebarComponentProps, StartNodeData } from '../types'
import { GenericSidebarComponent } from './sidebar.base'

type FormValues = StartNodeData['config']
interface SlackChannel {
  id: string
  name: string
  is_private: boolean
  purpose: string
  topic: string
}

// Node-level event callbacks the Start node supports (legacy PHLO). SER-4772.
const startEventTypes = [{ value: 'hangup', label: 'Hangup' }]

export const hasFormFields = (type: string | undefined): boolean => {
  if (!type) return false

  // These are the flow types that have form configurations
  const flowTypesWithFields = [
    FlowTypes.incoming_call,
    FlowTypes.api_request,
    FlowTypes.incoming_slack,
    FlowTypes.incoming_chat,
  ]

  return flowTypesWithFields.includes(type as FlowTypes)
}

export const fields = (
  nodeVars: string[],
  type: string | undefined,
  // `authToken` is optional on the wire — the backend omits it for callers that may not see it.
  authData: { authId: string; authToken?: string } | undefined,
  id?: string,
  createAppLink?: { app_creation_url: string },
): GenericFieldConfig<FormValues> => {
  // Node-level hangup callback also applies to omni Start (which otherwise
  // renders no field panel) — surface it so legacy omni callbacks stay visible
  // and editable. SER-4772.
  if (type === 'omni') {
    return [
      {
        name: 'events',
        type: 'slot',
        element: ({ form }) => (
          <div className="col-span-2">
            <form.Field name="events">
              {(field) => (
                <EventCallbacks
                  events={(field.state.value as EventCallback[]) ?? []}
                  onChange={(events) => field.handleChange(events)}
                  eventTypes={startEventTypes}
                  nodeVars={nodeVars}
                  className="mt-4"
                />
              )}
            </form.Field>
          </div>
        ),
      },
    ]
  }

  // Return empty array if no type or type doesn't have form fields
  if (!hasFormFields(type)) return []

  const validations = v2validations({ nodeVars }).start

  const SlackChannelField: React.FC<{ form: GenericFormType<StartNodeData['config']> }> = ({
    form,
  }) => {
    const {
      data: slackChannelsData,
      isLoading,
      isError,
    } = useGetSlackChannelsQuery(form.state.values.slack_bot_token || undefined)
    return (
      <Select
        label="Channel to watch"
        loading={isLoading}
        error={isError ? 'Unable to fetch channels' : undefined}
        options={(slackChannelsData?.channels || []).map((c: SlackChannel) => ({
          label: c.name,
          value: c.id,
        }))}
        value={form.state.values.slack_channel_id || undefined}
        onChange={(val) => {
          form.setFieldValue('slack_channel_id', val as string)
          form.validateField('slack_channel_id', 'change')
        }}
        placeholder="Select channel"
        data-testid="select-slack-channel"
      />
    )
  }

  // Now we can use switch statement since we know type has form fields
  switch (type) {
    case FlowTypes.incoming_call:
      return [
        {
          name: 'call_hangup_flow',
          label: 'Customer Hangup Agent',
          type: 'custom',
          component: ActiveFlowSelect as CustomCompType<FormValues>,
          eventDataExtractor: (e) => e as string,
          zodValidation: validations.shape.call_hangup_flow,
          inputProps: {
            placeholder: 'Select hangup agent',
            'data-testid': 'select-hangup-agent',
          },
        },
        {
          name: 'events',
          type: 'slot',
          element: ({ form }) => (
            <div className="col-span-2">
              <form.Field name="events">
                {(field) => (
                  <EventCallbacks
                    events={(field.state.value as EventCallback[]) ?? []}
                    onChange={(events) => field.handleChange(events)}
                    eventTypes={startEventTypes}
                    nodeVars={nodeVars}
                    className="mt-4"
                  />
                )}
              </form.Field>
            </div>
          ),
        },
      ]

    case FlowTypes.api_request:
      return [
        {
          name: 'url',
          label: 'API URL',
          zodValidation: validations.shape.url,
          type: 'custom',
          component: () => {
            const url = queryClient.getQueryData<FlowData>(flowQueryKeys().flowData(id || ''))
            const phloURL = url?.phlo?.url
            return (
              <Input
                type="text"
                inputMode="url"
                label="API URL"
                value={phloURL}
                placeholder="Save as Draft or Publish to set URL"
                disabled
                endIcon={<ClipboardCopyButton textToCopy={phloURL || ''} />}
              />
            )
          },
        },
        {
          name: 'require_http_auth',
          label: 'Require Authentication',
          componentType: 'switch',
          zodValidation: validations.shape.require_http_auth,
          inputProps: {
            description: 'When enabled, HTTP requests must include Basic Authentication.',
          },
        },
        {
          name: 'auth_id',
          zodValidation: validations.shape.auth_id,
          label: 'API Auth ID',
          componentType: 'input',
          onChangeListenTo: ['require_http_auth'],
          visible: (form) => form.require_http_auth,
          inputProps: {
            disabled: true,
            value: authData?.authId,
            endIcon: <ClipboardCopyButton textToCopy={authData?.authId || ''} />,
          },
        },
        {
          name: 'auth_token',
          zodValidation: validations.shape.auth_token,
          type: 'custom',
          visible: (form) => form.require_http_auth,
          onChangeListenTo: ['require_http_auth'],
          // `authData` undefined means the request is still in flight; a response that
          // arrived WITHOUT a token means this session isn't allowed to see it. Only the
          // second case gets the locked notice — an empty masked input there would offer a
          // reveal and a copy button that both produce an empty string.
          component: () =>
            authData && !authData.authToken ? (
              <RestrictedTokenField
                label="API Auth Token"
                testId="start-node-auth-token-restricted"
              />
            ) : (
              <PasswordInput
                label="API Auth Token"
                placeholder="Enter Auth Token"
                disabled
                value={authData?.authToken}
                endIcon={
                  <ClipboardCopyButton
                    textToCopy={authData?.authToken || ''}
                    data-testid="start-node-btn-copy-auth-token"
                  />
                }
              />
            ),
        },
        {
          name: 'api_request_details',
          type: 'basic',
          component: ({ form }) => (
            <div className="flex flex-col gap-2 col-span-2">
              <div className="text-h4-500">API Request Details</div>
              <div className="text-p-400 text-secondary">
                Enter the necessary information to trigger the API and ensure accurate responses.
              </div>
              <div className="text-s-500">Data that will be sent</div>
              <ApiRequestVarsWrapper form={form} />
            </div>
          ),
        },
        {
          name: 'events',
          type: 'slot',
          element: ({ form }) => (
            <div className="col-span-2">
              <form.Field name="events">
                {(field) => (
                  <EventCallbacks
                    events={(field.state.value as EventCallback[]) ?? []}
                    onChange={(events) => field.handleChange(events)}
                    eventTypes={startEventTypes}
                    nodeVars={nodeVars}
                    className="mt-4"
                  />
                )}
              </form.Field>
            </div>
          ),
        },
      ]

    case FlowTypes.incoming_slack:
      return [
        {
          name: 'slack_trigger_event',
          label: 'Trigger Event',
          componentType: 'select',
          inputProps: {
            placeholder: 'Select Trigger Event',
            options: [
              { label: 'On mention in a channel', value: SlackChannelTypes.MENTION },
              { label: 'On direct mention', value: SlackChannelTypes.DIRECT },
            ],
          },
          getDynamicProps: () => ({
            zodVal: z.string().refine((val: string) => val.trim() !== '', {
              message: 'Please select a trigger event',
            }),
          }),
          afterOnChange: (_v, _i, form) => {
            if (form) {
              form.setFieldValue('slack_channel_id', null)
              form.validateField('slack_channel_id', 'change')
            }
          },
        },
        {
          name: 'slack_bot_token',
          label: (
            <span className="flex items-center gap-2">
              Bot User OAuth Token
              <Tooltip content="Bot Token can be found at Slack App Dashboard → Basic Information → Install App">
                <Info className="w-4 h-4 text-muted-foreground" />
              </Tooltip>
            </span>
          ),
          type: 'custom',
          component: PasswordInput as unknown as CustomCompType<FormValues>,
          zodValidation: validations.shape.slack_bot_token.refine(
            (val: string | null | undefined) => val?.trim() !== '',
            {
              message: 'Slack bot token is required',
            },
          ),
          eventDataExtractor: (e) => e.target?.value as string,
          getDynamicProps: () => ({
            zodVal: z
              .string({ message: 'Slack bot token is required' })
              .refine((val: string) => val.trim() !== '', {
                message: 'Slack bot token is required',
              }),
          }),
          inputProps: {
            placeholder: 'Enter Bot Token',
          },
        },

        {
          name: 'slack_create_app_link',
          type: 'slot',
          component: (
            <span className="-mt-2 flex gap-1 text-tertiary items-center text-xs-400 col-span-2 w-full">
              Don&apos;t have an app?{' '}
              {createAppLink?.app_creation_url && (
                <a
                  href={createAppLink.app_creation_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline flex gap-2 items-center"
                >
                  Create App <ExternalLink className="w-4 h-4" />
                </a>
              )}
            </span>
          ),
        },
        // TODO: for now hiding from UI
        // {
        //   name: 'slack_signing_secret',
        //   label: (
        //     <span className="flex items-center gap-2">
        //       Signing Secret
        //       <Tooltip content="Signing Secret can be found at Slack App Dashboard → Basic Information → App Credentials → Signing Secret">
        //         <Info className="w-4 h-4 text-muted-foreground" />
        //       </Tooltip>
        //     </span>
        //   ),
        //   componentType: 'input',
        //   inputProps: {
        //     placeholder: 'Signing Secret',
        //     type: 'password',
        //   },
        //   getDynamicProps: (values: StartNodeData['config']) => {
        //     if (values.slack_trigger_event) {
        //       return {
        //         zodVal: z.string().refine((val: string) => val.trim() !== '', {
        //           message: 'Slack signing secret is required',
        //         }),
        //       }
        //     }
        //     return {
        //       zodVal: validations.shape.slack_signing_secret,
        //     }
        //   },
        // },
        {
          name: 'slack_channel_id',
          label: 'Channels to watch',
          type: 'custom',
          onChangeListenTo: ['slack_trigger_event', 'slack_bot_token'],
          visible: (form) => form.slack_trigger_event === SlackChannelTypes.MENTION,
          component: SlackChannelField,
          zodValidation: z.string({ message: 'Please select a channel' }),
        },
        {
          name: 'slack_reply_in_thread',
          label: 'Reply in thread',
          componentType: 'switch',
          zodValidation: validations.shape.slack_reply_in_thread,
          onChangeListenTo: ['slack_trigger_event'],
          visible: (form) => form.slack_trigger_event === SlackChannelTypes.DIRECT,
        },
      ]

    case FlowTypes.incoming_chat:
      return [
        {
          name: 'quick_response_questions_header',
          type: 'slot',
          component: (
            <div className="flex flex-col gap-2 mt-2 col-span-2">
              <div className="text-p-600">Quick response questions</div>
              <div className="text-p-400 text-secondary">
                Add common questions that users might ask. You can add up to 5 questions.
              </div>
            </div>
          ),
        },
        {
          name: 'enable_quick_responses',
          label: 'Show quick response questions',
          componentType: 'switch',
          zodValidation: validations.shape.enable_quick_responses,
        },
        {
          name: 'quick_response_questions',
          type: 'custom',
          visible: (form) => form.enable_quick_responses,
          zodValidation: (value: StartNodeData['config']) => {
            if (value.enable_quick_responses) {
              return validations.shape.quick_response_questions.refine(
                (questions) => (questions?.length || 0) > 0,
                {
                  message: 'Please add at least one question',
                },
              )
            }
            return validations.shape.quick_response_questions
          },
          onChangeListenTo: ['enable_quick_responses'],
          component: ({ form }) => (
            <div className="space-y-4 col-span-2">
              <DraggableList
                idKeyName={DragListIdKeys.id}
                list={form.state.values.quick_response_questions || []}
                className="border-none"
                onDragEnd={(reOrderedList: Array<{ id: string; value: string }>) => {
                  form.setFieldValue('quick_response_questions', reOrderedList)
                  form.validateField('quick_response_questions', 'change')
                }}
              >
                {(subItem: { id: string; value: string }) => (
                  <div className="flex items-stretch gap-2">
                    <Input
                      placeholder="Enter a question"
                      className="w-full"
                      value={subItem.value}
                      onChange={(e) => {
                        const updatedQuestions = form.state.values.quick_response_questions?.map(
                          (question) =>
                            question.id === subItem.id
                              ? { ...question, value: e.target.value }
                              : question,
                        )
                        form.setFieldValue('quick_response_questions', updatedQuestions)
                        form.validateField('quick_response_questions', 'change')
                      }}
                    />
                    <Button
                      variant="ghost"
                      type="button"
                      className="p-2"
                      onClick={() => {
                        const updatedQuestions = form.state.values.quick_response_questions?.filter(
                          (question) => question.id !== subItem.id,
                        )
                        form.setFieldValue('quick_response_questions', updatedQuestions)
                        form.validateField('quick_response_questions', 'change')
                      }}
                    >
                      <XIcon size={20} />
                    </Button>
                  </div>
                )}
              </DraggableList>
              <Button
                type="button"
                variant="ghost"
                disabled={(form.state.values.quick_response_questions?.length || 0) >= 5}
                className="self-start"
                onClick={() => {
                  form.setFieldValue('quick_response_questions', [
                    ...(form.state.values.quick_response_questions || []),
                    { id: uuidv4(), value: '' },
                  ])
                  if (!form.state.values.enable_quick_responses) {
                    form.setFieldValue('enable_quick_responses', true)
                  }
                }}
              >
                + Add Question
              </Button>
            </div>
          ),
        },
      ]

    default:
      return []
  }
}

export const StartSidebarComponent = (props: SidebarComponentProps<StartNodeData>) => {
  const { nodeVars, selectedNode } = props
  const type = useStartNodeType()
  const { id } = useParams()
  // A tagged flow shows/uses the SUBACCOUNT's own auth id + token here
  // (not the main account's) — see the hook for the token-source rationale.
  const { authData } = useStartNodeAuthData(id, type)
  const { data: createAppLink } = useGetSlackCreateAppLinkQuery(type || '')
  if (!type) return null
  const startFields = fields(nodeVars, type, authData, id, createAppLink)
  if (startFields.length === 0) return null
  return (
    <GenericSidebarComponent
      fields={startFields}
      {...props}
      defaultValues={
        selectedNode.data.config.slack_trigger_event
          ? {
              slack_trigger_event: selectedNode.data.config.slack_trigger_event,
              slack_reply_in_thread:
                selectedNode.data.config.slack_reply_in_thread ??
                selectedNode.data.config.slack_trigger_event === SlackChannelTypes.DIRECT,
            }
          : {}
      }
    />
  )
}
